import React from 'react'
import {
  useForTablet,
  IsEmbedded,
  NotEmbedded,
  Navbar,
} from '@sketch/components'
import { SketchHomeButton } from '@sketch/modules-common'
import BackButton, {
  useBackButton,
} from '../../../../shares/components/BackButton'
import HeaderBadge from '../../../../shares/components/HeaderBadge'
import { ShareWithoutVersion } from 'modules/versioning'
import * as Styles from './PrototypeHeader.styles'
import { PrototypeArtboardsNavigationItem } from 'modules/shares/components/HeaderNavigation/PrototypeArtboardsNavigationItem'
import { usePrototypeViewContext } from '../../hooks'
import { PrototypeResizeModeSelector } from '../PrototypeResizeModeSelector'
import { PrototypeFullScreenButton } from '../PrototypeFullScreenButton'
import { PrototypeViewArtboardIconLink } from '../PrototypeViewArtboard'
import { PrototypeControls } from '../PrototypeControls'

type PrototypeHeaderProps = {
  setSidebarLeftOpen: React.Dispatch<React.SetStateAction<boolean>>
  share: ShareWithoutVersion
}

export const PrototypeHeader: React.FC<PrototypeHeaderProps> = ({
  setSidebarLeftOpen,
  share,
}) => {
  const isTabletAndBigger = useForTablet()
  const { link: shouldRenderBackButton } = useBackButton()
  const { isFullscreen } = usePrototypeViewContext()

  if (isFullscreen) {
    return null
  }

  return (
    <Navbar data-testid="prototype-header">
      <NotEmbedded>
        <Navbar.Section>
          <HeaderBadge
            workspace={share.workspace}
            setSidebarLeftOpen={setSidebarLeftOpen}
          />
          {isTabletAndBigger && shouldRenderBackButton && (
            <Styles.NavbarBackButtonItem>
              <BackButton />
            </Styles.NavbarBackButtonItem>
          )}
          <PrototypeArtboardsNavigationItem />
        </Navbar.Section>
        {!isTabletAndBigger && (
          <Styles.NavbarItem>
            <PrototypeViewArtboardIconLink />
          </Styles.NavbarItem>
        )}
        {isTabletAndBigger && (
          <Navbar.Section align="end">
            <Styles.NavbarItem>
              <PrototypeControls />
            </Styles.NavbarItem>
            <Styles.NavbarItem>
              <PrototypeResizeModeSelector />
            </Styles.NavbarItem>
            <Styles.NavbarItem>
              <PrototypeViewArtboardIconLink />
            </Styles.NavbarItem>
            <Styles.NavbarItem>
              <PrototypeFullScreenButton />
            </Styles.NavbarItem>
          </Navbar.Section>
        )}
      </NotEmbedded>
      <IsEmbedded>
        <Navbar.Section>
          <SketchHomeButton />
          {shouldRenderBackButton && (
            <Styles.NavbarBackButtonItem>
              <BackButton />
            </Styles.NavbarBackButtonItem>
          )}
          <PrototypeArtboardsNavigationItem />
        </Navbar.Section>
      </IsEmbedded>
    </Navbar>
  )
}

export const PrototypeMobileFooter = () => {
  const { isFullscreen } = usePrototypeViewContext()

  if (isFullscreen) {
    return null
  }

  return (
    <Styles.MobileFooterNavbar data-testid="prototype-mobile-footer">
      <Navbar.Section align="end">
        <Styles.NavbarItem>
          <PrototypeControls />
        </Styles.NavbarItem>
        <Styles.NavbarItem>
          <PrototypeResizeModeSelector />
        </Styles.NavbarItem>
        <Styles.NavbarItem>
          <PrototypeFullScreenButton />
        </Styles.NavbarItem>
      </Navbar.Section>
    </Styles.MobileFooterNavbar>
  )
}

/**
 * Empty version of the prototype header to display
 * while the prototype is loading or if there is an error.
 */
export function PrototypeHeaderEmpty() {
  const { link: shouldRenderBackButton } = useBackButton()

  return (
    <Navbar data-testid="prototype-header-empty">
      <Navbar.Section>
        {shouldRenderBackButton && (
          <Styles.NavbarBackButtonItem>
            <BackButton />
          </Styles.NavbarBackButtonItem>
        )}
      </Navbar.Section>
    </Navbar>
  )
}
